import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import { Save, X } from "lucide-react";

import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import logoImage from "@/assets/greenvision-logo.png";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

import type {
  BatteryBrand,
  ChargerBrand,
  ChargerType,
  ConsumerType,
  InverterBrand,
  MeteringCompany,
  SmallConnectionOption,
} from "@/types/externalIntake";

const consumerTypeOptions: Array<{ label: string; value: ConsumerType }> = [
  { label: "Kleinverbruiker", value: "klein" },
  { label: "Grootverbruiker", value: "groot" },
];

const smallConnectionOptions: SmallConnectionOption[] = [
  "1x25A",
  "1x35A",
  "1x40A",
  "3x25A",
  "3x35A",
  "3x40A",
  "3x50A",
  "3x63A",
  "3x80A",
];

const meteringCompanyOptions: MeteringCompany[] = [
  "Anexo",
  "Enavi",
  "EVA Meet",
  "Fudura",
  "Innax",
  "Joulz",
  "Kenter",
  "Tums",
  "Anders",
];

const inverterBrandOptions: InverterBrand[] = ["Huawei", "Sigenergy", "SMA", "Sungrow", "Anders"];

const batteryBrandOptions: BatteryBrand[] = [
  "ABBR",
  "Alfen",
  "Ampowr",
  "Delta",
  "Huawei",
  "iWell",
  "Pixii",
  "Sigenergy",
  "Socomec",
  "Sungrow",
  "Anders",
];

const chargerBrandOptions: ChargerBrand[] = [
  "ABB",
  "Alfen",
  "Cube",
  "Easee",
  "Ecotap",
  "EVBox",
  "Peblar",
  "Wallbox",
  "Zaptec",
  "Anders",
];

const chargerTypeOptions: Array<{ label: string; value: ChargerType }> = [
  { label: "AC", value: "AC" },
  { label: "DC", value: "DC" },
  { label: "AC + DC", value: "AC_DC" },
];

// Base schema with conditional AC/DC validation
const baseSchema = z.object({
  contact: z.object({
    name: z.string().optional(),
    email: z.string().email("Vul een geldig e-mailadres in"),
    phone: z.string().optional(),
  }),
  location: z.object({
    name: z.string().min(1, "Locatienaam is verplicht"),
    street_house_number: z.string().min(1, "Straat + huisnummer is verplicht"),
    addition: z.string().optional(),
    postal_code: z.string().min(1, "Postcode is verplicht"),
    city: z.string().min(1, "Plaats is verplicht"),
  }),
  connection: z.object({
    consumer_type: z.enum(["klein", "groot"]),
    small_option: z
      .enum([
        "1x25A",
        "1x35A",
        "1x40A",
        "3x25A",
        "3x35A",
        "3x40A",
        "3x50A",
        "3x63A",
        "3x80A",
      ])
      .optional(),
    large_contract_delivery_kw: z.coerce.number().optional(),
    large_contract_feed_in_kw: z.coerce.number().optional(),
  }),
  metering: z.object({
    company: z.enum([
      "Anexo",
      "Enavi",
      "EVA Meet",
      "Fudura",
      "Innax",
      "Joulz",
      "Kenter",
      "Tums",
      "Anders",
    ]),
    company_other: z.string().optional(),
    meters_to_monitor: z.coerce.number().int().min(1, "Minimaal 1 meter"),
  }),
  solar: z.object({
    present: z.boolean(),
    total_power_kwp: z.coerce.number().optional(),
    inverter_brand: z.enum(["Huawei", "Sigenergy", "SMA", "Sungrow", "Anders"]).optional(),
    inverter_brand_other: z.string().optional(),
    inverter_count: z.coerce.number().int().optional(),
    datalogger_present: z.boolean().optional(),
    datalogger_brand: z.enum(["Huawei", "Sigenergy", "SMA", "Sungrow", "Anders"]).optional(),
    datalogger_brand_other: z.string().optional(),
  }),
  battery: z.object({
    present: z.boolean(),
    total_capacity_kwh: z.coerce.number().optional(),
    total_power_kw: z.coerce.number().optional(),
    brand: z
      .enum([
        "ABBR",
        "Alfen",
        "Ampowr",
        "Delta",
        "Huawei",
        "iWell",
        "Pixii",
        "Sigenergy",
        "Socomec",
        "Sungrow",
        "Anders",
      ])
      .optional(),
    brand_other: z.string().optional(),
    pms_present: z.boolean().optional(),
  }),
  charging: z.object({
    present: z.boolean(),
    type: z.enum(["AC", "DC", "AC_DC"]).optional(),
    ac: z
      .object({
        total_power_kw: z.coerce.number().optional(),
        count: z.coerce.number().int().optional(),
        brand: z
          .enum([
            "ABB",
            "Alfen",
            "Cube",
            "Easee",
            "Ecotap",
            "EVBox",
            "Peblar",
            "Wallbox",
            "Zaptec",
            "Anders",
          ])
          .optional(),
        brand_other: z.string().optional(),
      })
      .optional(),
    dc: z
      .object({
        total_power_kw: z.coerce.number().optional(),
        count: z.coerce.number().int().optional(),
        brand: z
          .enum([
            "ABB",
            "Alfen",
            "Cube",
            "Easee",
            "Ecotap",
            "EVBox",
            "Peblar",
            "Wallbox",
            "Zaptec",
            "Anders",
          ])
          .optional(),
        brand_other: z.string().optional(),
      })
      .optional(),
    cpo: z.enum(["EFlux", "Evesto", "Greenflux", "LMS", "Anders"]).optional(),
    cpo_other: z.string().optional(),
  }),
  generators: z.object({
    present: z.boolean(),
    max_total_power_kw: z.coerce.number().optional(),
  }),
  notes: z.object({
    expected_growth: z.string().optional(),
    sustainability_goals: z.string().optional(),
  }),
}).superRefine((data, ctx) => {
  // Validate AC/DC specs when charging is present
  if (data.charging.present) {
    if (!data.charging.type) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Selecteer het type laden (AC/DC/AC+DC)",
        path: ["charging", "type"],
      });
      return;
    }

    const needsAC = data.charging.type === "AC" || data.charging.type === "AC_DC";
    const needsDC = data.charging.type === "DC" || data.charging.type === "AC_DC";

    if (needsAC) {
      if (!data.charging.ac?.total_power_kw || data.charging.ac.total_power_kw <= 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Totaal AC vermogen is verplicht",
          path: ["charging", "ac", "total_power_kw"],
        });
      }
      if (!data.charging.ac?.count || data.charging.ac.count <= 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Aantal AC laders is verplicht",
          path: ["charging", "ac", "count"],
        });
      }
    }

    if (needsDC) {
      if (!data.charging.dc?.total_power_kw || data.charging.dc.total_power_kw <= 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Totaal DC vermogen is verplicht",
          path: ["charging", "dc", "total_power_kw"],
        });
      }
      if (!data.charging.dc?.count || data.charging.dc.count <= 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Aantal DC laders is verplicht",
          path: ["charging", "dc", "count"],
        });
      }
    }
  }
});

type FormValues = z.infer<typeof baseSchema>;

type LookupState =
  | { status: "idle" | "loading" }
  | { status: "ok"; project_id: string | null; draft_payload: FormValues | null; draft_updated_at: string | null }
  | { status: "error"; message: string };

type RequiredFileKey =
  | "kwartierwaarden_hoofdmeter"
  | "kwartierwaarden_productie"
  | "energiecontract"
  | "transportovereenkomst";

const optionalFiles: Array<{ key: RequiredFileKey; label: string; accept?: string }> = [
  { key: "kwartierwaarden_hoofdmeter", label: "Kwartierwaarden hoofdmeter (optioneel)" },
  { key: "kwartierwaarden_productie", label: "Kwartierwaarden productie (optioneel)" },
  { key: "energiecontract", label: "Energiecontract (optioneel)" },
  { key: "transportovereenkomst", label: "Transportovereenkomst (optioneel)" },
];

const defaultFormValues: FormValues = {
  contact: { name: "", email: "", phone: "" },
  location: { name: "", street_house_number: "", addition: "", postal_code: "", city: "" },
  connection: { consumer_type: "klein", small_option: "3x25A" },
  metering: { company: "Anders", company_other: "", meters_to_monitor: 1 },
  solar: { present: false },
  battery: { present: false },
  charging: { present: false },
  generators: { present: false },
  notes: { expected_growth: "", sustainability_goals: "" },
};

export default function ExternalIntakeWizard() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [lookup, setLookup] = useState<LookupState>({ status: "loading" });
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(0);
  const [files, setFiles] = useState<Record<RequiredFileKey, File | null>>({
    kwartierwaarden_hoofdmeter: null,
    kwartierwaarden_productie: null,
    energiecontract: null,
    transportovereenkomst: null,
  });
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(baseSchema),
    defaultValues: defaultFormValues,
    mode: "onBlur",
  });

  const draftLoaded = useRef(false);

  // Auto-save mutation
  const saveDraftMutation = useMutation({
    mutationFn: async (payload: FormValues) => {
      if (!token) throw new Error("No token");
      const { error } = await supabase.functions.invoke("intake-save-draft", {
        body: { token, payload },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      setLastSavedAt(new Date());
      setIsSaving(false);
    },
    onError: (err) => {
      console.error("[ExternalIntakeWizard] draft save failed", err);
      setIsSaving(false);
    },
  });

  // Debounced auto-save
  const formValues = form.watch();
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const saveDraft = useCallback(() => {
    if (!token || submitting) return;
    setIsSaving(true);
    saveDraftMutation.mutate(formValues);
  }, [token, submitting, formValues, saveDraftMutation]);

  // Auto-save on form changes (debounced)
  useEffect(() => {
    if (!draftLoaded.current) return; // Don't save until draft is loaded
    if (lookup.status !== "ok") return;

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(() => {
      saveDraft();
    }, 3000);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [formValues, saveDraft, lookup.status]);

  // Save on step change
  const handleStepChange = useCallback((newStep: number) => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveDraft();
    setStep(newStep);
  }, [saveDraft]);

  const steps = useMemo(
    () => [
      { id: "basis", title: "Contact & locatie" },
      { id: "aansluiting", title: "Aansluiting & metering" },
      { id: "assets", title: "Opwek & assets" },
      { id: "uploads", title: "Documenten" },
      { id: "review", title: "Controleren & verzenden" },
    ] as const,
    [],
  );

  useEffect(() => {
    if (!token) {
      setLookup({ status: "error", message: "Ongeldige link (token ontbreekt)." });
      return;
    }

    let mounted = true;
    (async () => {
      try {
        setLookup({ status: "loading" });
        const { data, error } = await supabase.functions.invoke("intake-lookup", {
          body: { token },
        });
        if (error) throw error;

        const ok = (data as any)?.ok;
        if (!ok) {
          throw new Error((data as any)?.error || "Link kan niet worden gevalideerd.");
        }

        const draftPayload = (data as any)?.draft_payload ?? null;
        const draftUpdatedAt = (data as any)?.draft_updated_at ?? null;

        if (mounted) {
          setLookup({ 
            status: "ok", 
            project_id: (data as any)?.project_id ?? null,
            draft_payload: draftPayload,
            draft_updated_at: draftUpdatedAt,
          });

          // Load draft if exists
          if (draftPayload && typeof draftPayload === "object") {
            form.reset({ ...defaultFormValues, ...draftPayload });
            if (draftUpdatedAt) {
              setLastSavedAt(new Date(draftUpdatedAt));
            }
          }
          draftLoaded.current = true;
        }
      } catch (e: any) {
        console.error("[ExternalIntakeWizard] lookup failed", e);
        const message =
          typeof e?.message === "string" && e.message
            ? e.message
            : "Deze intake link is ongeldig of niet meer beschikbaar.";
        if (mounted) setLookup({ status: "error", message });
      }
    })();

    return () => {
      mounted = false;
    };
  }, [token, form]);

  const progressLabel = `${step + 1}/${steps.length}`;

  const next = async () => {
    // Per-step validation including charging specs on step 2
    const fieldsByStep: Record<number, Array<any>> = {
      0: ["contact.email", "location.name", "location.street_house_number", "location.postal_code", "location.city"],
      1: ["connection.consumer_type", "metering.company", "metering.meters_to_monitor"],
      2: ["charging.type", "charging.ac.total_power_kw", "charging.ac.count", "charging.dc.total_power_kw", "charging.dc.count"],
      3: [],
      4: [],
    };

    const ok = await form.trigger(fieldsByStep[step] ?? []);
    if (!ok) return;
    handleStepChange(Math.min(steps.length - 1, step + 1));
  };

  const prev = () => handleStepChange(Math.max(0, step - 1));

  const onSubmit = async (values: FormValues) => {
    if (!token) return;

    try {
      setSubmitting(true);

      const formData = new FormData();
      formData.append("token", token);
      formData.append("payload", JSON.stringify(values));

      // Only append files that are actually selected
      for (const rf of optionalFiles) {
        const f = files[rf.key];
        if (f) formData.append(rf.key, f);
      }

      const { data, error } = await supabase.functions.invoke("intake-submit", {
        body: formData,
      });

      if (error) throw error;
      if (!(data as any)?.ok) throw new Error((data as any)?.error || "Onbekende fout bij verzenden");

      navigate("/intake/success", { replace: true });
    } catch (e: any) {
      console.error("[ExternalIntakeWizard] submit failed", e);
      const msg =
        typeof e?.message === "string" && e.message
          ? e.message
          : "Verzenden mislukt. Probeer het opnieuw.";
      toast({ title: "Fout", description: msg, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  const handleLeave = () => {
    saveDraft();
    setShowLeaveDialog(false);
  };

  // Watch charging type for conditional rendering
  const chargingPresent = form.watch("charging.present");
  const chargingType = form.watch("charging.type");
  const needsAC = chargingType === "AC" || chargingType === "AC_DC";
  const needsDC = chargingType === "DC" || chargingType === "AC_DC";

  if (lookup.status === "loading" || lookup.status === "idle") {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#014336]/5 to-background">
        <header className="w-full py-6 px-4 border-b border-[#014336]/10">
          <div className="max-w-3xl mx-auto">
            <img src={logoImage} alt="GreenVision Energy" className="h-10 sm:h-12" />
          </div>
        </header>
        <div className="flex items-center justify-center p-4 pt-20">
          <Card className="w-full max-w-2xl border-[#014336]/20">
            <CardHeader>
              <CardTitle className="text-[#014336]">Intake laden…</CardTitle>
              <CardDescription>We controleren de link.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-sm text-muted-foreground">Even geduld…</div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (lookup.status === "error") {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#014336]/5 to-background">
        <header className="w-full py-6 px-4 border-b border-[#014336]/10">
          <div className="max-w-3xl mx-auto">
            <img src={logoImage} alt="GreenVision Energy" className="h-10 sm:h-12" />
          </div>
        </header>
        <div className="flex items-center justify-center p-4 pt-20">
          <Card className="w-full max-w-2xl border-[#014336]/20">
            <CardHeader>
              <CardTitle className="text-[#014336]">Intake link niet beschikbaar</CardTitle>
              <CardDescription>{lookup.message}</CardDescription>
            </CardHeader>
            <CardContent className="flex items-center justify-end">
              <Button variant="outline" onClick={() => window.close()}>
                Sluiten
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#014336]/5 to-background">
      {/* Branded Header */}
      <header className="w-full py-6 px-4 border-b border-[#014336]/10 bg-white/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <img src={logoImage} alt="GreenVision Energy" className="h-10 sm:h-12" />
          <div className="flex items-center gap-2">
            {isSaving ? (
              <Badge variant="outline" className="text-xs border-[#014336]/30 text-[#014336]">
                <Save className="h-3 w-3 mr-1 animate-pulse" />
                Opslaan…
              </Badge>
            ) : lastSavedAt ? (
              <Badge variant="secondary" className="text-xs bg-[#014336]/10 text-[#014336]">
                <Save className="h-3 w-3 mr-1" />
                Opgeslagen
              </Badge>
            ) : null}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex items-start justify-center p-4 sm:p-6 pt-6">
        <div className="w-full max-w-3xl space-y-4">
          {/* Progress Card */}
          <Card className="border-[#014336]/20">
            <CardHeader className="space-y-2 pb-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <CardTitle className="text-[#014336]">EMS Intake</CardTitle>
                  <CardDescription>
                    Vul de gegevens in en upload de 4 documenten. Stap {progressLabel}.
                  </CardDescription>
                </div>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={() => setShowLeaveDialog(true)}
                  title="Sluiten en later verder"
                  className="text-[#014336] hover:bg-[#014336]/10"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              {/* Progress indicator */}
              <div className="flex gap-1.5 pt-2">
                {steps.map((s, i) => (
                  <div
                    key={i}
                    className={`h-1.5 flex-1 rounded-full transition-colors ${
                      i < step ? 'bg-[#014336]' : i === step ? 'bg-[#014336]/60' : 'bg-[#014336]/15'
                    }`}
                  />
                ))}
              </div>
              <div className="text-xs text-[#014336]/70 font-medium">{steps[step]?.title}</div>
            </CardHeader>
          </Card>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">{steps[step]?.title}</CardTitle>
                {lookup.status === 'ok' && lookup.project_id ? (
                  <CardDescription>Project gekoppeld</CardDescription>
                ) : (
                  <CardDescription>Deze intake is gekoppeld aan een project in het CRM.</CardDescription>
                )}
              </CardHeader>
              <CardContent className="space-y-6">
                {step === 0 ? (
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <FormField
                        control={form.control}
                        name="contact.name"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Naam (optioneel)</FormLabel>
                            <FormControl>
                              <Input placeholder="Naam" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="contact.email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>E-mail *</FormLabel>
                            <FormControl>
                              <Input placeholder="naam@bedrijf.nl" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="contact.phone"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Telefoon (optioneel)</FormLabel>
                            <FormControl>
                              <Input placeholder="06…" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <div className="space-y-4">
                      <div className="text-sm font-medium">Locatie</div>
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <FormField
                          control={form.control}
                          name="location.name"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Locatienaam *</FormLabel>
                              <FormControl>
                                <Input placeholder="Vestiging …" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="location.street_house_number"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Straat + huisnummer *</FormLabel>
                              <FormControl>
                                <Input placeholder="Straat 1" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="location.addition"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Toevoeging (optioneel)</FormLabel>
                              <FormControl>
                                <Input placeholder="A" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="location.postal_code"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Postcode *</FormLabel>
                              <FormControl>
                                <Input placeholder="1234 AB" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="location.city"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Plaats *</FormLabel>
                              <FormControl>
                                <Input placeholder="Amsterdam" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>
                  </div>
                ) : null}

                {step === 1 ? (
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <FormField
                        control={form.control}
                        name="connection.consumer_type"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Type aansluiting *</FormLabel>
                            <Select value={field.value} onValueChange={field.onChange}>
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Kies…" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {consumerTypeOptions.map((o) => (
                                  <SelectItem key={o.value} value={o.value}>
                                    {o.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      {form.watch("connection.consumer_type") === "klein" ? (
                        <FormField
                          control={form.control}
                          name="connection.small_option"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Kleinverbruiker optie</FormLabel>
                              <Select value={field.value} onValueChange={field.onChange}>
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Kies…" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {smallConnectionOptions.map((v) => (
                                    <SelectItem key={v} value={v}>
                                      {v}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      ) : (
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                          <FormField
                            control={form.control}
                            name="connection.large_contract_delivery_kw"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Contractvermogen afname (kW)</FormLabel>
                                <FormControl>
                                  <Input type="number" inputMode="decimal" placeholder="Bijv. 250" {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name="connection.large_contract_feed_in_kw"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Contractvermogen teruglevering (kW)</FormLabel>
                                <FormControl>
                                  <Input type="number" inputMode="decimal" placeholder="Bijv. 100" {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                      )}
                    </div>

                    <div className="space-y-4">
                      <div className="text-sm font-medium">Meetbedrijf</div>
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <FormField
                          control={form.control}
                          name="metering.company"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Meetbedrijf *</FormLabel>
                              <Select value={field.value} onValueChange={field.onChange}>
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Kies…" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {meteringCompanyOptions.map((v) => (
                                    <SelectItem key={v} value={v}>
                                      {v}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="metering.meters_to_monitor"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Aantal meters te monitoren *</FormLabel>
                              <FormControl>
                                <Input type="number" inputMode="numeric" min={1} {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        {form.watch("metering.company") === "Anders" ? (
                          <FormField
                            control={form.control}
                            name="metering.company_other"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Meetbedrijf (anders)</FormLabel>
                                <FormControl>
                                  <Input placeholder="Naam meetbedrijf" {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        ) : null}
                      </div>
                    </div>
                  </div>
                ) : null}

                {step === 2 ? (
                  <div className="space-y-8">
                    {/* Solar Section */}
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <FormField
                        control={form.control}
                        name="solar.present"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Zonnestroom aanwezig?</FormLabel>
                            <FormControl>
                              <Select value={field.value ? "ja" : "nee"} onValueChange={(v) => field.onChange(v === "ja")}>
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="nee">Nee</SelectItem>
                                  <SelectItem value="ja">Ja</SelectItem>
                                </SelectContent>
                              </Select>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      {form.watch("solar.present") ? (
                        <FormField
                          control={form.control}
                          name="solar.total_power_kwp"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Totaal vermogen PV (kWp)</FormLabel>
                              <FormControl>
                                <Input type="number" inputMode="decimal" placeholder="Bijv. 200" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      ) : null}
                    </div>

                    {form.watch("solar.present") ? (
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <FormField
                          control={form.control}
                          name="solar.inverter_brand"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Omvormer merk</FormLabel>
                              <Select value={field.value} onValueChange={field.onChange}>
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Kies…" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {inverterBrandOptions.map((v) => (
                                    <SelectItem key={v} value={v}>
                                      {v}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        {form.watch("solar.inverter_brand") === "Anders" ? (
                          <FormField
                            control={form.control}
                            name="solar.inverter_brand_other"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Omvormer merk (anders)</FormLabel>
                                <FormControl>
                                  <Input placeholder="Merk" {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        ) : null}
                        <FormField
                          control={form.control}
                          name="solar.inverter_count"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Aantal omvormers</FormLabel>
                              <FormControl>
                                <Input type="number" inputMode="numeric" placeholder="Bijv. 2" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    ) : null}

                    {/* Battery Section */}
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <FormField
                        control={form.control}
                        name="battery.present"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Batterij aanwezig?</FormLabel>
                            <FormControl>
                              <Select value={field.value ? "ja" : "nee"} onValueChange={(v) => field.onChange(v === "ja")}>
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="nee">Nee</SelectItem>
                                  <SelectItem value="ja">Ja</SelectItem>
                                </SelectContent>
                              </Select>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      {form.watch("battery.present") ? (
                        <FormField
                          control={form.control}
                          name="battery.total_capacity_kwh"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Totaal capaciteit (kWh)</FormLabel>
                              <FormControl>
                                <Input type="number" inputMode="decimal" placeholder="Bijv. 500" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      ) : null}
                    </div>

                    {form.watch("battery.present") ? (
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <FormField
                          control={form.control}
                          name="battery.brand"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Batterij merk</FormLabel>
                              <Select value={field.value} onValueChange={field.onChange}>
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Kies…" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {batteryBrandOptions.map((v) => (
                                    <SelectItem key={v} value={v}>
                                      {v}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        {form.watch("battery.brand") === "Anders" ? (
                          <FormField
                            control={form.control}
                            name="battery.brand_other"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Batterij merk (anders)</FormLabel>
                                <FormControl>
                                  <Input placeholder="Merk" {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        ) : null}
                        <FormField
                          control={form.control}
                          name="battery.total_power_kw"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Totaal vermogen (kW)</FormLabel>
                              <FormControl>
                                <Input type="number" inputMode="decimal" placeholder="Bijv. 250" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    ) : null}

                    {/* Charging Section - Now with required AC/DC specs */}
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <FormField
                        control={form.control}
                        name="charging.present"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Laadinfrastructuur aanwezig?</FormLabel>
                            <FormControl>
                              <Select value={field.value ? "ja" : "nee"} onValueChange={(v) => field.onChange(v === "ja")}>
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="nee">Nee</SelectItem>
                                  <SelectItem value="ja">Ja</SelectItem>
                                </SelectContent>
                              </Select>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      {chargingPresent ? (
                        <FormField
                          control={form.control}
                          name="charging.type"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Type laden *</FormLabel>
                              <Select value={field.value} onValueChange={field.onChange}>
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Kies…" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {chargerTypeOptions.map((o) => (
                                    <SelectItem key={o.value} value={o.value}>
                                      {o.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      ) : null}
                    </div>

                    {chargingPresent && needsAC ? (
                      <div className="space-y-4">
                        <div className="text-sm font-medium">AC laden (verplicht)</div>
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                          <FormField
                            control={form.control}
                            name="charging.ac.total_power_kw"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Totaal AC (kW) *</FormLabel>
                                <FormControl>
                                  <Input type="number" inputMode="decimal" placeholder="Bijv. 44" {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name="charging.ac.count"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Aantal AC *</FormLabel>
                                <FormControl>
                                  <Input type="number" inputMode="numeric" placeholder="Bijv. 4" {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name="charging.ac.brand"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>AC merk</FormLabel>
                                <Select value={field.value} onValueChange={field.onChange}>
                                  <FormControl>
                                    <SelectTrigger>
                                      <SelectValue placeholder="Kies…" />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent>
                                    {chargerBrandOptions.map((v) => (
                                      <SelectItem key={v} value={v}>
                                        {v}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          {form.watch("charging.ac.brand") === "Anders" ? (
                            <FormField
                              control={form.control}
                              name="charging.ac.brand_other"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>AC merk (anders)</FormLabel>
                                  <FormControl>
                                    <Input placeholder="Merk" {...field} />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          ) : null}
                        </div>
                      </div>
                    ) : null}

                    {chargingPresent && needsDC ? (
                      <div className="space-y-4">
                        <div className="text-sm font-medium">DC laden (verplicht)</div>
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                          <FormField
                            control={form.control}
                            name="charging.dc.total_power_kw"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Totaal DC (kW) *</FormLabel>
                                <FormControl>
                                  <Input type="number" inputMode="decimal" placeholder="Bijv. 300" {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name="charging.dc.count"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Aantal DC *</FormLabel>
                                <FormControl>
                                  <Input type="number" inputMode="numeric" placeholder="Bijv. 2" {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name="charging.dc.brand"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>DC merk</FormLabel>
                                <Select value={field.value} onValueChange={field.onChange}>
                                  <FormControl>
                                    <SelectTrigger>
                                      <SelectValue placeholder="Kies…" />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent>
                                    {chargerBrandOptions.map((v) => (
                                      <SelectItem key={v} value={v}>
                                        {v}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          {form.watch("charging.dc.brand") === "Anders" ? (
                            <FormField
                              control={form.control}
                              name="charging.dc.brand_other"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>DC merk (anders)</FormLabel>
                                  <FormControl>
                                    <Input placeholder="Merk" {...field} />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          ) : null}
                        </div>
                      </div>
                    ) : null}

                    {/* Generators Section */}
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <FormField
                        control={form.control}
                        name="generators.present"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Noodaggregaat / generator aanwezig?</FormLabel>
                            <FormControl>
                              <Select value={field.value ? "ja" : "nee"} onValueChange={(v) => field.onChange(v === "ja")}>
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="nee">Nee</SelectItem>
                                  <SelectItem value="ja">Ja</SelectItem>
                                </SelectContent>
                              </Select>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      {form.watch("generators.present") ? (
                        <FormField
                          control={form.control}
                          name="generators.max_total_power_kw"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Max. totaal vermogen (kW)</FormLabel>
                              <FormControl>
                                <Input type="number" inputMode="decimal" placeholder="Bijv. 500" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      ) : null}
                    </div>
                  </div>
                ) : null}

                {step === 3 ? (
                  <div className="space-y-4">
                    <FormDescription>
                      Documenten zijn optioneel. Je kunt de intake ook verzenden zonder bijlagen en deze later aanleveren.
                    </FormDescription>
                    <div className="space-y-4">
                      {optionalFiles.map((rf) => {
                        const current = files[rf.key];
                        return (
                          <div key={rf.key} className="rounded-md border p-3 space-y-2">
                            <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                              <div className="font-medium">{rf.label}</div>
                              <div className="text-sm text-muted-foreground">
                                {current ? current.name : "Nog geen bestand gekozen"}
                              </div>
                            </div>
                            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                              <div>
                                <Label htmlFor={`file-${rf.key}`} className="text-sm">
                                  Bestand kiezen
                                </Label>
                                <Input
                                  id={`file-${rf.key}`}
                                  type="file"
                                  onChange={(e) => {
                                    const f = e.target.files?.[0] ?? null;
                                    setFiles((prev) => ({ ...prev, [rf.key]: f }));
                                  }}
                                />
                              </div>
                              <div className="flex items-end">
                                <Button
                                  type="button"
                                  variant="outline"
                                  disabled={!current}
                                  onClick={() => setFiles((prev) => ({ ...prev, [rf.key]: null }))}
                                >
                                  Verwijder
                                </Button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {Object.values(files).filter(Boolean).length} van {optionalFiles.length} documenten toegevoegd
                    </div>
                  </div>
                ) : null}

                {step === 4 ? (
                  <div className="space-y-3">
                    <div className="text-sm text-muted-foreground">
                      Controleer de gegevens. Klik daarna op <span className="font-medium">Verzenden</span>.
                    </div>
                    <div className="rounded-md border p-3 space-y-2">
                      <div className="text-sm">
                        <span className="text-muted-foreground">Contact: </span>
                        {form.getValues("contact.email")}
                      </div>
                      <div className="text-sm">
                        <span className="text-muted-foreground">Locatie: </span>
                        {form.getValues("location.name")} – {form.getValues("location.city")}
                      </div>
                      <div className="text-sm">
                        <span className="text-muted-foreground">Aansluiting: </span>
                        {form.getValues("connection.consumer_type") === "klein" ? "Kleinverbruiker" : "Grootverbruiker"}
                      </div>
                      <div className="text-sm">
                        <span className="text-muted-foreground">Documenten: </span>
                        {Object.values(files).filter(Boolean).length} van {optionalFiles.length} toegevoegd
                      </div>
                    </div>
                  </div>
                ) : null}
              </CardContent>
            </Card>

            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <Button type="button" variant="outline" onClick={prev} disabled={step === 0 || submitting}>
                Vorige
              </Button>
              <div className="text-xs text-muted-foreground">Stap {progressLabel}: {steps[step]?.title}</div>
              {step < steps.length - 1 ? (
                <Button type="button" onClick={next} disabled={submitting}>
                  Volgende
                </Button>
              ) : (
                <Button type="submit" disabled={submitting}>
                  {submitting ? "Verzenden…" : "Verzenden"}
                </Button>
              )}
            </div>
          </form>
        </Form>
        </div>
      </div>

      <AlertDialog open={showLeaveDialog} onOpenChange={setShowLeaveDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Intake afsluiten?</AlertDialogTitle>
            <AlertDialogDescription>
              Je voortgang wordt automatisch opgeslagen. Je kunt later terugkomen met dezelfde link om verder te gaan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction onClick={handleLeave}>
              Opslaan en sluiten
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
